import React from 'react';
import { createRoot } from 'react-dom/client';
import { MultiSelect } from 'primereact/multiselect';
import { Checkbox } from 'primereact/checkbox';
import { normalizeForMatch } from './textNormalize';
import { uiT } from './uiTranslate';
import sk from '../locales/sk.json';
import en from '../locales/en.json';
import de from '../locales/de.json';

/** Display strings for Common.FilterAll across locales (never use as filter RAW). */
const FILTER_ALL_DISPLAY = new Set(
  [sk['Common.FilterAll'], en['Common.FilterAll'], de['Common.FilterAll']]
    .filter((v) => typeof v === 'string' && v.trim() !== '')
    .map(String)
);

/**
 * True when `value` is a Common.FilterAll display snapshot (SK/EN/DE).
 * Used to detect stale factory-time placeholders after locale change.
 */
export function isCommonFilterAllDisplay(value) {
  if (value == null || value === '') return false;
  return FILTER_ALL_DISPLAY.has(String(value));
}

/**
 * Resolve the MultiSelect empty-state placeholder from current runtime locale.
 *
 * Tabulator creates header-filter editors once; `setColumns` updates column defs but does
 * NOT remount editors. Factory-time `editorParams.placeholder` can remain a SK snapshot
 * ("Všetko") while React column state already has All/Alles — so we must resolve live.
 *
 * @param {{ placeholder?: string, placeholderKey?: string }|null|undefined} editorParams
 * @returns {string}
 */
export function resolvePrimeMultiSelectPlaceholder(editorParams) {
  const key = editorParams?.placeholderKey;
  if (typeof key === 'string' && key.trim() !== '') {
    return uiT(key);
  }
  const snapshot = editorParams?.placeholder;
  // Missing/empty OR any FilterAll locale snapshot → live Common.FilterAll
  if (snapshot == null || snapshot === '' || isCommonFilterAllDisplay(snapshot)) {
    return uiT('Common.FilterAll');
  }
  // Custom placeholders (e.g. Expiration) keep explicit translated snapshot
  return String(snapshot);
}

/**
 * Exclusive multi-select tokens (e.g. "Všetko"): selecting exclusive clears concretes;
 * selecting a concrete while exclusive is active drops exclusive.
 * @param {unknown[]} next
 * @param {unknown[]} prev
 * @param {unknown[]} exclusiveValues
 * @returns {unknown[]}
 */
export function applyExclusiveMultiSelectValues(next, prev = [], exclusiveValues = []) {
  const exclusive = (exclusiveValues || []).map((x) => String(x));
  if (!exclusive.length) return Array.isArray(next) ? [...next] : [];
  let out = Array.isArray(next) ? [...next] : [];
  for (const ex of exclusive) {
    const others = out.filter((v) => String(v) !== ex);
    const nowHas = out.some((v) => String(v) === ex);
    const prevHad = (prev || []).some((v) => String(v) === ex);
    if (nowHas && others.length > 0) {
      out = prevHad ? others : [ex];
    }
  }
  return out;
}

/**
 * Serialize MultiSelect selection for Tabulator header filter.
 * Exclusive-only selection becomes '' so the request omits the filter param.
 * @param {unknown[]} values
 * @param {{ exclusiveValues?: unknown[] }} [opts]
 */
export function serializeMultiSelectFilterValue(values, opts = {}) {
  const list = Array.isArray(values) ? values.map((v) => String(v)) : [];
  if (!list.length) return '';
  const exclusive = (opts.exclusiveValues || []).map((x) => String(x));
  if (exclusive.length && list.every((v) => exclusive.includes(v))) return '';
  return list.join(',');
}

/**
 * Normalize saved/restored CSV so exclusive tokens do not coexist with concrete buckets.
 * Exclusive-only → empty (caller skips / clears). Mixed → drop exclusive, keep concretes.
 * @param {unknown} raw
 * @param {unknown[]} [exclusiveValues=['Všetko']]
 */
export function normalizeExclusiveFilterCsv(raw, exclusiveValues = ['Všetko']) {
  if (raw == null || raw === '') return '';
  const exclusive = new Set((exclusiveValues || []).map((x) => String(x)));
  const parts = String(raw)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (!parts.length) return '';
  const hasExclusive = parts.some((p) => exclusive.has(p));
  const concretes = parts.filter((p) => !exclusive.has(p));
  if (hasExclusive && concretes.length === 0) return '';
  if (hasExclusive && concretes.length > 0) return concretes.join(',');
  return parts.join(',');
}

/**
 * Spoločný Tabulator header filter – MultiSelect s normalizovaným vyhľadávaním (bez diakritiky).
 * Wrapper má rovnaké štýly ako built-in input filter v Tabulator 5.5:
 *   padding: 4px; width: 100%; box-sizing: border-box; (výška z vnútorného prvku).
 * Použitie: headerFilter: primeMultiSelectHeaderFilter, headerFilterParams: { options, placeholder?, valueType?, exclusiveValues?, minWidth?, maxWidth? }.
 */
export function primeMultiSelectHeaderFilter(cell, onRendered, success, cancel, editorParams) {
  const mount = document.createElement('div');
  mount.style.padding = '0';
  mount.style.width = 'calc(100% - 6px)';
  mount.style.minWidth = 'calc(100% - 6px)';
  mount.style.maxWidth = 'calc(100% - 6px)';
  mount.style.boxSizing = 'border-box';
  mount.style.margin = '0';
  mount.style.position = 'relative';
  /* šírka vždy stĺpec mínus 6px – pravý border nikdy orezaný; minWidth z params sa nepoužíva na wrapper */

  const rawOptions = editorParams?.options || [];
  const options = rawOptions.filter(
    (o) => o?.value != null && o?.value !== 'null' && o?.value !== 'undefined'
  );
  // Do NOT capture placeholder here — Tabulator editors are not remounted on setColumns /
  // locale change; resolve live inside HeaderMulti via resolvePrimeMultiSelectPlaceholder.
  const valueType = editorParams?.valueType ?? 'string';
  const exclusiveValues = Array.isArray(editorParams?.exclusiveValues)
    ? editorParams.exclusiveValues
    : [];
  const editorParamsRef = { current: editorParams || {} };

  const parseValue = (v) => {
    const allowedValues = new Set(
      options
        .map((o) => o?.value)
        .filter((x) => x != null && x !== '' && x !== 'null' && x !== 'undefined')
        .map((x) => (valueType === 'number' ? Number(x) : String(x)))
        .filter((x) => (valueType === 'number' ? !Number.isNaN(x) : true))
    );
    if (v === undefined || v === null || v === '') return [];
    if (Array.isArray(v))
      return valueType === 'number'
        ? v.map(Number).filter((n) => !isNaN(n) && allowedValues.has(n))
        : v
            .map((x) => String(x))
            .filter((x) => x != null && x !== 'null' && x !== 'undefined' && allowedValues.has(String(x)));
    const parts = String(v)
      .split(',')
      .map((x) => x.trim())
      .filter((x) => x && x !== 'null' && x !== 'undefined');
    const parsed = valueType === 'number'
      ? parts.map((x) => Number(x)).filter((n) => !isNaN(n) && allowedValues.has(n))
      : parts.filter((x) => allowedValues.has(String(x)));
    if (!exclusiveValues.length) return parsed;
    const normalizedCsv = normalizeExclusiveFilterCsv(parsed.join(','), exclusiveValues);
    if (!normalizedCsv) {
      // Exclusive-only → empty UI selection (all / no filter)
      const onlyExclusive = parsed.length > 0 && parsed.every((p) => exclusiveValues.map(String).includes(String(p)));
      return onlyExclusive ? [] : parsed;
    }
    return valueType === 'number'
      ? normalizedCsv.split(',').map(Number).filter((n) => !isNaN(n))
      : normalizedCsv.split(',').filter(Boolean);
  };

  const optionsRef = { current: options };
  const successRef = { current: success };
  const exclusiveRef = { current: exclusiveValues };
  let root = null;
  onRendered(() => {
    root = createRoot(mount);
    const initial = parseValue(cell.getValue());
    const HeaderMulti = () => {
      const selectAllId = React.useId();
      const [val, setVal] = React.useState(initial);
      const [filterText, setFilterText] = React.useState('');
      // Re-render on app locale change so placeholder resolves from current uiT locale
      // without remounting the Tabulator header filter editor.
      const [localeEpoch, setLocaleEpoch] = React.useState(0);
      React.useEffect(() => {
        const bump = () => setLocaleEpoch((n) => n + 1);
        window.addEventListener('app-ui-locale-changed', bump);
        window.addEventListener('app-locale-preferences-updated', bump);
        return () => {
          window.removeEventListener('app-ui-locale-changed', bump);
          window.removeEventListener('app-locale-preferences-updated', bump);
        };
      }, []);
      const livePlaceholder = React.useMemo(
        () => resolvePrimeMultiSelectPlaceholder(editorParamsRef.current),
        [localeEpoch]
      );
      const filteredOptions = React.useMemo(() => {
        const opts = optionsRef.current || [];
        const n = normalizeForMatch(filterText);
        if (!n) return opts;
        return opts.filter((o) => {
          const label = (o?.label ?? o?.value ?? '').toString();
          return normalizeForMatch(label).includes(n);
        });
      }, [filterText]);
      const onChange = React.useCallback((e) => {
        const exclusive = exclusiveRef.current || [];
        const next = applyExclusiveMultiSelectValues(e.value || [], val, exclusive);
        // Exclusive-only (Všetko) ⇒ clear selection; request omits filter.
        const exclusiveOnly =
          exclusive.length > 0 &&
          next.length > 0 &&
          next.every((v) => exclusive.map(String).includes(String(v)));
        const resolved = exclusiveOnly ? [] : next;
        setVal(resolved);
        const valueToSend = serializeMultiSelectFilterValue(resolved, { exclusiveValues: exclusive });
        requestAnimationFrame(() => {
          successRef.current(valueToSend);
        });
      }, [val]);
      const allOptionValues = (optionsRef.current || [])
        .map((o) => o.value)
        .filter((v) => v != null && v !== '' && v !== 'null' && v !== 'undefined');
      const selectAllChecked = allOptionValues.length > 0 && allOptionValues.every((v) => val.includes(v));
      const selectAllIndeterminate = val.length > 0 && !selectAllChecked;
      const toggleSelectAll = React.useCallback(() => {
        const exclusive = exclusiveRef.current || [];
        const opts = (optionsRef.current || [])
          .map((o) => o.value)
          .filter((v) => v != null && v !== '' && v !== 'null' && v !== 'undefined');
        const allSel = opts.length > 0 && opts.every((v) => val.includes(v));
        const raw = allSel ? [] : [...opts];
        // Exclusive Všetko + all concretes → normalize to clear (no filter), same as Všetko alone.
        const next = applyExclusiveMultiSelectValues(raw, val, exclusive);
        const resolved =
          exclusive.length && next.length === 1 && exclusive.map(String).includes(String(next[0]))
            ? []
            : next;
        setVal(resolved);
        const valueToSend = serializeMultiSelectFilterValue(resolved, { exclusiveValues: exclusive });
        requestAnimationFrame(() => {
          successRef.current(valueToSend);
        });
      }, [val]);
      const panelHeaderTemplate = React.useCallback(({ closeElement, onCloseClick }) => (
        <div className="flex items-center gap-2 p-2 border-b border-surface-200">
          <div className="flex items-center shrink-0 px-0.5" title={uiT('PrimeMultiSelect.SelectAllTitle')}>
            <Checkbox
              inputId={selectAllId}
              checked={selectAllChecked}
              indeterminate={selectAllIndeterminate}
              onChange={toggleSelectAll}
              aria-label={uiT('PrimeMultiSelect.SelectAllAria')}
            />
          </div>
          <input
            type="text"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            placeholder={uiT('PrimeMultiSelect.FilterNoDiacritics')}
            className="flex-1 min-w-0 p-2 text-sm border border-surface-300 rounded"
          />
          <button type="button" onClick={onCloseClick} aria-label={uiT('Common.Close')}>
            {closeElement}
          </button>
        </div>
      ), [filterText, selectAllChecked, selectAllIndeterminate, toggleSelectAll, localeEpoch]);
      return (
        <MultiSelect
          value={val}
          options={filteredOptions}
          optionLabel="label"
          optionValue="value"
          placeholder={livePlaceholder}
          display="comma"
          filter={false}
          panelHeaderTemplate={panelHeaderTemplate}
          showClear
          className="w-full p-inputtext-sm tabulator-header-ms"
          style={{ minHeight: '34px', height: '34px', width: '100%', maxWidth: '100%', minWidth: 0 }}
          panelStyle={{ minWidth: '100px' }}
          onChange={onChange}
        />
      );
    };
    root.render(<HeaderMulti />);
  });
  mount._destroy = () => {
    try {
      root?.unmount?.();
    } catch (_) {}
  };
  return mount;
}
